import { deleteEntity, updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'

import { normalize } from 'normalizr'

import api from '@functions/api'

import type {
  Action,
  DeleteParams,
  EntityOptions,
  Errors,
  FetchRequestPayload,
  RootReducerState,
} from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import type { BrandCustomProfileModel } from '@models/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/brandCustomProfile/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/brandCustomProfile/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/brandCustomProfile/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/brandCustomProfile/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/brandCustomProfile/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/brandCustomProfile/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/brandCustomProfile/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/brandCustomProfile/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/brandCustomProfile/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/brandCustomProfile/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/brandCustomProfile/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/brandCustomProfile/DELETE_FAILURE'

export type BrandCustomProfileModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: BrandCustomProfileModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadBrandCustomProfiles(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().brandCustomProfiles.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/brand_custom_profiles.json', options)
      .then(({ data }: { data: BrandCustomProfileModel[] }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_CUSTOM_PROFILE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createBrandCustomProfile(brandCustomProfile:
  Partial<BrandCustomProfileModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      brand_custom_profile: brandCustomProfile,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/brand_custom_profiles.json', options, config)
      .then(({ data }: { data: BrandCustomProfileModel }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_CUSTOM_PROFILE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateBrandCustomProfile(brandCustomProfile:
  Partial<BrandCustomProfileModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      brand_custom_profile: brandCustomProfile,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/brand_custom_profiles/${brandCustomProfile.id}.json`, options, config)
      .then(({ data }: { data: BrandCustomProfileModel }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_CUSTOM_PROFILE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteBrandCustomProfile(brandCustomProfile: DeleteParams<BrandCustomProfileModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/brand_custom_profiles/${brandCustomProfile.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_CUSTOM_PROFILE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: BrandCustomProfileModuleState = initialState,
  action: Action = { type: '' },
): BrandCustomProfileModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
